import { useState } from "react";
import { FaUtensils } from "react-icons/fa";
import { FiGrid, FiInfo, FiSearch, FiShoppingBag } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { categoryIconsMap } from "../../../config/categoryIcons";
import { formatPersianPrice, getCategoryKey, parseTableNumber } from "../../../services/customerMenuModel";
import MenuIntro from "../components/MenuIntro";
import MenuSplash from "../components/MenuSplash";

const persianNumber = new Intl.NumberFormat("fa-IR");
const GOLD = "#C9A44C";
const DARK = "#2B2620";

const LuxuryItemCard = ({ item, quantity = 0, onAdd, onDecrease, onClick }) => (
  <article className="flex gap-3 border-b py-4" style={{ borderColor: "#EFE9DA" }}>
    <button type="button" onClick={() => onClick(item)} className="h-20 w-20 shrink-0 overflow-hidden rounded-lg" style={{ backgroundColor: "#F6F1E6" }}>
      {item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full w-full items-center justify-center text-2xl" style={{ color: GOLD }}><FaUtensils /></span>}
    </button>
    <button type="button" onClick={() => onClick(item)} className="min-w-0 flex-1 text-right focus:outline-none">
      <h3 className="font-serif text-base font-bold" style={{ color: DARK }}>{item.name}</h3>
      {item.description && <p className="mt-1 line-clamp-1 text-xs text-gray-400">{item.description}</p>}
      <p className="mt-2 text-sm font-black" style={{ color: GOLD }}>{formatPersianPrice(item.price)}</p>
    </button>
    <div className="flex shrink-0 items-end">
      {quantity > 0 ? (
        <div className="flex items-center gap-1.5 rounded-full border px-1 py-1" style={{ borderColor: GOLD }}>
          <button type="button" onClick={() => onDecrease(item)} aria-label={`کم کردن ${item.name}`} className="flex h-7 w-7 items-center justify-center" style={{ color: GOLD }}>−</button>
          <span className="w-4 text-center text-xs font-bold" style={{ color: DARK }}>{persianNumber.format(quantity)}</span>
          <button type="button" onClick={() => onAdd(item)} aria-label={`اضافه کردن ${item.name}`} className="flex h-7 w-7 items-center justify-center" style={{ color: GOLD }}>+</button>
        </div>
      ) : (
        <button type="button" onClick={() => onAdd(item)} aria-label={`افزودن ${item.name} به سبد`} className="flex h-9 w-9 items-center justify-center rounded-full border text-sm" style={{ borderColor: GOLD, color: GOLD }}>+</button>
      )}
    </div>
  </article>
);

const LuxuryWhiteMenuTheme = (props) => {
  const {
    menuData, restaurant, settings, profileImage, coverImage, categories, visibleItems,
    activeCategoryKey, setActiveCategoryKey, searchTerm, setSearchTerm, quantities, cartSummary,
    addItem, decreaseItem, openItem, openCart, showBamizbanBadge,
  } = props;
  const [infoOpen, setInfoOpen] = useState(false);
  const location = useLocation();
  const tableNumber = parseTableNumber(location.search);

  return (
    <div className="min-h-screen pb-28" dir="rtl" style={{ backgroundColor: "#FFFDF9" }}>
      <MenuSplash
        restaurantName={restaurant.name || menuData.title}
        profileImage={profileImage}
        backgroundColor={DARK}
        accentColor={GOLD}
        tagline="تجربه‌ای متفاوت از طعم"
        titleFont="font-serif"
      />
      <section className="relative h-52 overflow-hidden sm:h-60">
        <img src={coverImage} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
          <button type="button" onClick={() => setInfoOpen(true)} aria-label="اطلاعات رستوران" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow" style={{ color: GOLD }}><FiInfo /></button>
          {tableNumber && <span className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow" style={{ color: DARK }}>میز {persianNumber.format(tableNumber)}</span>}
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-4">
        <div className="relative mx-auto -mt-10 mb-2 text-center">
          <img src={profileImage} alt="" className="mx-auto h-16 w-16 rounded-full border-4 bg-white object-cover shadow" style={{ borderColor: "#FFFDF9" }} />
          <h1 className="mt-2 font-serif text-xl font-black" style={{ color: DARK }}>{restaurant.name || menuData.title}</h1>
          <div className="mx-auto mt-1 h-px w-12" style={{ backgroundColor: GOLD }} />
          {settings.welcomeText && <p className="mt-2 text-xs leading-6 text-gray-500">{settings.welcomeText}</p>}
        </div>

        <div className="sticky top-0 z-30 mx-auto -mx-4 bg-[#FFFDF9]/95 px-4 pb-3 pt-2 backdrop-blur">
          <label className="relative block">
            <span className="sr-only">جست‌وجوی منو</span>
            <FiSearch className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
            <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="جست‌وجوی غذا" className="min-h-12 w-full rounded-full border py-3 pl-4 pr-11 text-sm outline-none focus:ring-2" style={{ borderColor: "#EFE9DA", "--tw-ring-color": GOLD }} />
          </label>

          <div className="mt-4 flex gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="دسته‌بندی‌های منو">
            <button type="button" aria-pressed={activeCategoryKey === "all"} onClick={() => setActiveCategoryKey("all")} className="flex shrink-0 flex-col items-center gap-1.5">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border text-lg" style={activeCategoryKey === "all" ? { backgroundColor: GOLD, color: "#fff", borderColor: GOLD } : { borderColor: "#EFE9DA", color: GOLD }}><FiGrid /></span>
              <span className="text-[11px] font-bold" style={{ color: DARK }}>همه</span>
            </button>
            {categories.map((category) => {
              const key = getCategoryKey(category);
              const Icon = categoryIconsMap[category.icon] || FaUtensils;
              const active = activeCategoryKey === key;
              return (
                <button key={key} type="button" aria-pressed={active} onClick={() => setActiveCategoryKey(key)} className="flex shrink-0 flex-col items-center gap-1.5">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border text-lg" style={active ? { backgroundColor: GOLD, color: "#fff", borderColor: GOLD } : { borderColor: "#EFE9DA", color: GOLD }}><Icon /></span>
                  <span className="max-w-16 truncate text-[11px] font-bold" style={{ color: DARK }}>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {visibleItems.length ? (
          <div className="divide-y" style={{ borderColor: "#EFE9DA" }}>
            {visibleItems.map((item) => (
              <LuxuryItemCard key={item._id} item={item} quantity={quantities[item._id]} onAdd={addItem} onDecrease={decreaseItem} onClick={openItem} />
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed px-4 py-14 text-center" style={{ borderColor: "#EFE9DA" }}>
            <FaUtensils className="mx-auto text-3xl" style={{ color: "#E4D9BC" }} aria-hidden="true" />
            <p className="mt-3 font-bold" style={{ color: DARK }}>آیتمی پیدا نشد</p>
            <p className="mt-1 text-sm text-gray-400">عبارت جست‌وجو یا دسته‌بندی را تغییر دهید.</p>
            {searchTerm && <button type="button" onClick={() => { setSearchTerm(""); setActiveCategoryKey("all"); }} className="mt-4 text-sm font-bold" style={{ color: GOLD }}>پاک کردن جست‌وجو</button>}
          </div>
        )}

        {showBamizbanBadge && <p className="pb-2 pt-6 text-center text-[11px] text-gray-300">ساخته شده با <span className="font-bold text-gray-400">بامیزبان</span></p>}
      </main>

      {cartSummary.itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3" dir="rtl">
          <button type="button" onClick={openCart} className="mx-auto flex min-h-12 w-full max-w-xl items-center justify-between rounded-full px-5 font-bold text-white shadow-lg" style={{ backgroundColor: DARK }}>
            <span className="flex items-center gap-2"><FiShoppingBag /> مشاهده سبد <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: GOLD }}>{persianNumber.format(cartSummary.itemCount)} آیتم</span></span>
            <span>{formatPersianPrice(cartSummary.total)}</span>
          </button>
        </div>
      )}

      <MenuIntro open={infoOpen} restaurantData={restaurant} profileImage={profileImage} onClose={() => setInfoOpen(false)} />
    </div>
  );
};

export default LuxuryWhiteMenuTheme;
